import * as React from 'react';
import { Clock, Users, Disc } from 'lucide-react';
import { EMPTY_VALUE, formatCount } from '@/lib/format';
import type { MeetingAnalytics, RecordingStatus } from '../types';
import { durationOrDash } from '../format';

/** The headline metrics for the call, as a responsive tile row. */
export function StatTiles({ data }: { data: MeetingAnalytics }) {
  const stillInCall = data.attendance.filter((a) => a.stillJoined).length;
  const rec = recordingStat(data.recordingStatus, data.recordingDurationSeconds);

  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <StatTile
        icon={<Clock className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />}
        label="Call duration"
        value={durationOrDash(data.durationSeconds)}
      />
      <StatTile
        icon={<Users className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />}
        label="Participants"
        value={formatCount(data.attendance.length)}
        sub={stillInCall > 0 ? `${formatCount(stillInCall)} still in call` : undefined}
      />
      <StatTile
        icon={<Disc className="h-[1.15rem] w-[1.15rem]" aria-hidden="true" />}
        label="Recording"
        value={rec.value}
        sub={rec.sub}
      />
    </div>
  );
}

function recordingStat(status: RecordingStatus, seconds: number): { value: string; sub: string } {
  switch (status) {
    case 'completed':
      return { value: durationOrDash(seconds), sub: 'Ready to play' };
    case 'recording':
      return { value: EMPTY_VALUE, sub: 'In progress' };
    case 'processing':
      return { value: EMPTY_VALUE, sub: 'Processing' };
    case 'failed':
      return { value: EMPTY_VALUE, sub: 'Failed' };
    case 'none':
    default:
      return { value: EMPTY_VALUE, sub: 'Not recorded' };
  }
}

function StatTile({
  icon,
  label,
  value,
  sub,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <div className="flex items-start gap-3.5 rounded-[var(--radius-md)] border border-[var(--border)] bg-[var(--surface)] p-4 shadow-[var(--shadow-sm)]">
      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[var(--radius-sm)] bg-[var(--primary-soft)] text-[var(--primary)]">
        {icon}
      </span>
      <div className="min-w-0">
        <p className="text-[0.6875rem] font-semibold uppercase tracking-[0.06em] text-[var(--text-muted)]">
          {label}
        </p>
        <p className="mt-1 font-heading text-xl font-semibold tabular-nums leading-none text-[var(--text-primary)]">
          {value}
        </p>
        {sub && <p className="mt-1.5 truncate text-xs text-[var(--text-muted)]">{sub}</p>}
      </div>
    </div>
  );
}
